import { format } from 'date-fns'

export type ExportSizePreset =
  | 'view2'
  | 'view4'
  | 'letter'
  | 'archA'
  | 'tabloid'
  | 'archB'
  | 'ansiC'
  | 'archC'
  | 'ansiD'
  | 'archD'
  | 'archE1'
  | 'ansiE'
  | 'archE'
  | 'a4'
  | 'a3'
  | 'a2'
  | 'a1'
  | 'a0'

export type ExportViewPreset = 'view2' | 'view4'
export type ExportPaperPreset = Exclude<ExportSizePreset, ExportViewPreset>

export enum ExportOrientation {
  Landscape = 'landscape',
  Portrait = 'portrait',
}

export enum ExportQuality {
  Standard = 150,
  High = 300,
}

// Standard and High. Past 2x a Screen export only sharpens what is drawn (labels, icons, lines,
// site plans): tile zoom has no pixel-ratio term, so the imagery is the same @2x tiles.
export const EXPORT_VIEW_PRESETS: ExportViewPreset[] = ['view2', 'view4']

// ANSI and ARCH, smallest to largest by area.
export const IMPERIAL_PAPER_PRESETS: ExportPaperPreset[] = [
  'letter',
  'archA',
  'tabloid',
  'archB',
  'ansiC',
  'archC',
  'ansiD',
  'archD',
  'archE1',
  'ansiE',
  'archE',
]

export const METRIC_PAPER_PRESETS: ExportPaperPreset[] = [
  'a4',
  'a3',
  'a2',
  'a1',
  'a0',
]

export const EXPORT_PAPER_PRESETS: ExportPaperPreset[] = [
  ...IMPERIAL_PAPER_PRESETS,
  ...METRIC_PAPER_PRESETS,
]

export const EXPORT_SIZE_PRESETS: ExportSizePreset[] = [
  ...EXPORT_VIEW_PRESETS,
  ...EXPORT_PAPER_PRESETS,
]

export const PAPER_INCHES: Record<ExportPaperPreset, [number, number]> = {
  letter: [11, 8.5],
  archA: [12, 9],
  tabloid: [17, 11],
  archB: [18, 12],
  ansiC: [22, 17],
  archC: [24, 18],
  ansiD: [34, 22],
  archD: [36, 24],
  archE1: [42, 30],
  ansiE: [44, 34],
  archE: [48, 36],
  a4: [11.6929, 8.2677],
  a3: [16.5354, 11.6929],
  a2: [23.3858, 16.5354],
  a1: [33.1102, 23.3858],
  a0: [46.811, 33.1102],
}

// The ANSI designation of a sheet better known by its name; the menu shows both, "ANSI A (Letter)".
export const FAMILIAR_NAME_DESIGNATION: Partial<
  Record<ExportPaperPreset, string>
> = {
  letter: 'ANSI A',
  tabloid: 'ANSI B',
}

export const VIEW_PIXEL_RATIOS: Record<ExportViewPreset, number> = {
  view2: 2,
  view4: 4,
}

export type ExportScreenShape = '16x9' | '16x10' | '4x3'

export const SCREEN_SHAPES: ExportScreenShape[] = ['16x9', '16x10', '4x3']

// A Screen page in css px, long side then short, like PAPER_INCHES. One long side for every shape,
// so labels keep one size against the image whatever the shape, and the same on every machine:
// 1280 x 720 is a widescreen slide at 96 css px an inch.
export const SCREEN_CSS: Record<ExportScreenShape, [number, number]> = {
  '16x9': [1280, 720],
  '16x10': [1280, 800],
  '4x3': [1280, 960],
}

export const CSS_PX_PER_INCH = 96
const PT_PER_INCH = 72
export const MM_PER_INCH = 25.4

export interface IExportLayout {
  cssWidth: number
  cssHeight: number
  pixelRatio: number
  pxWidth: number
  pxHeight: number
  isPaper: boolean
  widthIn?: number
  heightIn?: number
}

// The shape whose map area (the page less the band) is nearest the window's in aspect, compared
// as a ratio. None while the window is unmeasured, or when it stands the other way.
export function nearestScreenShape(
  viewCss: { width: number; height: number },
  bandCssHeight: number,
  orientation: ExportOrientation,
): ExportScreenShape | null {
  if (!(viewCss.width > 0 && viewCss.height > 0)) return null
  const isLandscape = orientation === ExportOrientation.Landscape
  if (viewCss.width >= viewCss.height !== isLandscape) return null
  const aspect = Math.log(viewCss.width / viewCss.height)
  const distance = (shape: ExportScreenShape): number => {
    const [long, short] = SCREEN_CSS[shape]
    const width = isLandscape ? long : short
    const height = (isLandscape ? short : long) - bandCssHeight
    return Math.abs(aspect - Math.log(width / height))
  }
  return SCREEN_SHAPES.reduce((best, shape) =>
    distance(shape) < distance(best) ? shape : best,
  )
}

export const isPaperPreset = (
  preset: ExportSizePreset,
): preset is ExportPaperPreset => !(preset in VIEW_PIXEL_RATIOS)

export function resolveExportLayout(input: {
  preset: ExportSizePreset
  orientation: ExportOrientation
  quality: ExportQuality
  screenShape: ExportScreenShape
}): IExportLayout {
  const { preset, orientation, quality, screenShape } = input
  const isLandscape = orientation === ExportOrientation.Landscape
  if (!isPaperPreset(preset)) {
    const [long, short] = SCREEN_CSS[screenShape]
    const cssWidth = isLandscape ? long : short
    const cssHeight = isLandscape ? short : long
    const pixelRatio = VIEW_PIXEL_RATIOS[preset]
    return {
      cssWidth,
      cssHeight,
      pixelRatio,
      pxWidth: cssWidth * pixelRatio,
      pxHeight: cssHeight * pixelRatio,
      isPaper: false,
    }
  }

  const [longIn, shortIn] = PAPER_INCHES[preset]
  const widthIn = isLandscape ? longIn : shortIn
  const heightIn = isLandscape ? shortIn : longIn
  const cssWidth = Math.round(widthIn * CSS_PX_PER_INCH)
  const cssHeight = Math.round(heightIn * CSS_PX_PER_INCH)
  // canvas.width truncates a fractional size, so a ratio landing a hair under the target would
  // lose a whole pixel; a quarter pixel of headroom keeps the floor on the target.
  const pixelRatio = (Math.round(widthIn * quality) + 0.25) / cssWidth

  return {
    cssWidth,
    cssHeight,
    pixelRatio,
    pxWidth: Math.floor(cssWidth * pixelRatio),
    pxHeight: Math.floor(cssHeight * pixelRatio),
    isPaper: true,
    widthIn,
    heightIn,
  }
}

export function paperDimensions(
  preset: ExportPaperPreset,
  orientation: ExportOrientation,
): { width: number; height: number; unit: 'mm' | 'in' } {
  const isMetric = METRIC_PAPER_PRESETS.includes(preset)
  const toUnit = (inches: number): number =>
    isMetric ? Math.round(inches * MM_PER_INCH) : Math.round(inches * 10) / 10
  const [long, short] = PAPER_INCHES[preset].map(toUnit)
  const isLandscape = orientation === ExportOrientation.Landscape
  return {
    width: isLandscape ? long : short,
    height: isLandscape ? short : long,
    unit: isMetric ? 'mm' : 'in',
  }
}

export function pagePointsFor(layout: IExportLayout): {
  widthPt: number
  heightPt: number
} {
  if (layout.isPaper) {
    return {
      widthPt: layout.widthIn * PT_PER_INCH,
      heightPt: layout.heightIn * PT_PER_INCH,
    }
  }
  const ptPerCssPx = PT_PER_INCH / CSS_PX_PER_INCH
  return {
    widthPt: layout.cssWidth * ptPerCssPx,
    heightPt: layout.cssHeight * ptPerCssPx,
  }
}

// The raster is stretched across the whole page, so anything laid out in layout px lands on it
// at this one factor.
export const pagePointsPerCssPx = (layout: IExportLayout): number =>
  pagePointsFor(layout).widthPt / layout.cssWidth

// The map and, when page elements are on, the band under it. A sheet keeps its size, paper or
// Screen, so the map gives up the band's height.
export interface IExportSheet {
  page: IExportLayout
  map: IExportLayout
  bandCssHeight: number
  bandPxHeight: number
}

export function exportSheetFor(
  page: IExportLayout,
  bandCssHeight: number,
): IExportSheet {
  const cssHeight = page.cssHeight - bandCssHeight
  const pxHeight = Math.floor(cssHeight * page.pixelRatio)
  return {
    page,
    map: { ...page, cssHeight, pxHeight },
    bandCssHeight,
    bandPxHeight: page.pxHeight - pxHeight,
  }
}

export function sheetPagePoints(sheet: IExportSheet): {
  widthPt: number
  heightPt: number
  mapHeightPt: number
  scale: number
} {
  const { widthPt, heightPt } = pagePointsFor(sheet.page)
  const scale = pagePointsPerCssPx(sheet.page)
  return {
    widthPt,
    heightPt,
    mapHeightPt: sheet.map.cssHeight * scale,
    scale,
  }
}

export function fitFrame(
  pane: { width: number; height: number },
  aspect: number,
  insetPx: number,
): { left: number; top: number; width: number; height: number } {
  const availableWidth = Math.max(0, pane.width - insetPx * 2)
  const availableHeight = Math.max(0, pane.height - insetPx * 2)
  const isHeightLimited = availableWidth / availableHeight > aspect
  const width = isHeightLimited ? availableHeight * aspect : availableWidth
  const height = isHeightLimited ? availableHeight : availableWidth / aspect

  return {
    left: (pane.width - width) / 2,
    top: (pane.height - height) / 2,
    width,
    height,
  }
}

export interface IExportRect {
  left: number
  top: number
  width: number
  height: number
}

// The map frame stays centred in the pane, so the camera's centre is the export's; the band hangs
// below it, and the same height is kept free above so it always fits.
export function fitSheetFrame(
  pane: { width: number; height: number },
  sheet: IExportSheet,
  insetPx: number,
): { map: IExportRect; band: IExportRect } {
  const { cssWidth, cssHeight } = sheet.map
  const outer = fitFrame(
    pane,
    cssWidth / (cssHeight + 2 * sheet.bandCssHeight),
    insetPx,
  )
  const bandHeight = (sheet.bandCssHeight * outer.width) / cssWidth
  const map = {
    left: outer.left,
    top: outer.top + bandHeight,
    width: outer.width,
    height: outer.height - 2 * bandHeight,
  }
  return {
    map,
    band: { ...map, top: map.top + map.height, height: bandHeight },
  }
}

// Pane px per preview map css px. The preview map is laid out in sheet units and CSS-scaled into
// the pane, so its icons, lines and basemap labels sit at the file's scale and its zoom is the
// export's. Floored where the unscaled container's backing store would pass the device's largest
// canvas: the frame then holds more map px than the sheet, and the conversions still hold.
export const MAX_PREVIEW_CANVAS_AREA = 32_000_000

export function previewScaleFor(input: {
  frameWidth: number
  sheetCssWidth: number
  pane: { width: number; height: number }
  pixelRatio: number
  deviceMaxSide: number | null
  maxCanvasArea: number
}): number {
  const {
    frameWidth,
    sheetCssWidth,
    pane,
    pixelRatio,
    deviceMaxSide,
    maxCanvasArea,
  } = input
  const sheetScale = frameWidth / sheetCssWidth
  // Chrome silently shrinks a WebGL canvas resized past ~5760² device px, while mapbox keeps laying
  // the map out for the full size: the map then draws larger than its own projection and every
  // overlay drifts off it. The preview canvas is resized after creation, so it stays under that.
  const areaFloor =
    pixelRatio * Math.sqrt((pane.width * pane.height) / maxCanvasArea)
  if (deviceMaxSide === null) {
    return Math.max(sheetScale, areaFloor)
  }
  // Mapbox sizes the buffer as ratio × ceil(css), so a css px of headroom.
  const maxCss = deviceMaxSide / pixelRatio - 1
  return Math.max(
    sheetScale,
    areaFloor,
    Math.max(pane.width, pane.height) / maxCss,
  )
}

// The offset a page dragged at actual size may take along one axis: it never leaves more than the
// inset between a page edge and the pane's. A page smaller than the pane moves between the two.
export function clampPageOffset(
  offset: number,
  start: number,
  end: number,
  paneSize: number,
  inset: number,
): number {
  const toEnd = paneSize - inset - end
  const toStart = inset - start
  return Math.min(
    Math.max(offset, Math.min(toEnd, toStart)),
    Math.max(toEnd, toStart),
  )
}

export const exportZoomFor = (
  previewZoom: number,
  frameCssWidth: number,
  layoutCssWidth: number,
): number => previewZoom + Math.log2(layoutCssWidth / frameCssWidth)

// Mapbox sets the camera's distance from the canvas height, and the preview canvas is the whole
// pane while the file's is only the frame: narrowing the file's field of view by the same ratio
// keeps its camera at the preview's distance, so a tilted file foreshortens like the frame.
export const exportFovFor = (
  previewFovDeg: number,
  frameHeight: number,
  previewCanvasHeight: number,
): number => {
  const halfFov = (previewFovDeg * Math.PI) / 360
  return (
    (360 / Math.PI) *
    Math.atan((Math.tan(halfFov) * frameHeight) / previewCanvasHeight)
  )
}

// Frames in the preview map's css px: a zoom level is a fixed amount of ground per map px, so the
// zoom that fits the framed ground into the reshaped frame does not depend on the pane.
export const zoomKeepingGround = (
  framed: { width: number; height: number; zoom: number },
  after: { width: number; height: number },
): number =>
  framed.zoom +
  Math.log2(Math.min(after.width / framed.width, after.height / framed.height))

export function seedPreviewZoom(
  liveZoom: number,
  liveCss: { width: number; height: number },
  frame: { width: number; height: number },
): number {
  return (
    liveZoom +
    Math.log2(
      Math.min(frame.width / liveCss.width, frame.height / liveCss.height),
    )
  )
}

const sanitizeFileNamePart = (part: string): string =>
  (part ?? '').replace(/[\\/:*?"<>|\s]+/g, '-').replace(/^-+|-+$/g, '')

export function exportFileName(
  projectName: string,
  viewName: string,
  date: Date,
  extension: 'png' | 'pdf',
): string {
  const name = [projectName, viewName, format(date, 'yyyy-MM-dd')]
    .map(sanitizeFileNamePart)
    .filter(Boolean)
    .join('-')
  return `${name}.${extension}`
}
